package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.shared.ui.AutosizeRows
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

private const val FALLBACK_FONT_PX = 14.0
private const val FALLBACK_LINE_RATIO = 1.5
private const val TEXTAREA_LINE_HEIGHT = "1.5"

private fun cssPx(value: String): Double = value.removeSuffix("px").toDoubleOrNull() ?: 0.0

private fun fitToContent(element: HTMLElement, minRows: Int, maxRows: Int) {
    val computed = window.getComputedStyle(element)
    val fontSize = cssPx(computed.fontSize).takeIf { it > 0.0 } ?: FALLBACK_FONT_PX
    val lineHeight = cssPx(computed.lineHeight).takeIf { it > 0.0 } ?: (fontSize * FALLBACK_LINE_RATIO)
    val padding = cssPx(computed.paddingTop) + cssPx(computed.paddingBottom)
    val border = cssPx(computed.borderTopWidth) + cssPx(computed.borderBottomWidth)
    element.style.height = "auto"
    val result = AutosizeRows.resolve(element.scrollHeight.toDouble(), padding, border, lineHeight, minRows, maxRows)
    element.style.height = "${result.heightPx}px"
    element.style.overflowY = if (result.scrolls) "auto" else "hidden"
}

@Composable
fun Textarea(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    help: String? = null,
    error: String? = null,
    placeholder: String = "",
    disabled: Boolean = false,
    rows: Int = 4,
    minHeightPx: Int = 100,
    resize: Boolean = true,
    ariaLabel: String? = null,
    size: ControlSize = ControlSize.Md,
    hint: String? = null,
    required: Boolean = false,
    autosize: Boolean = false,
    minRows: Int = 3,
    maxRows: Int = 8,
    reserveMessageSpace: Boolean = false,
) {
    val metrics = size.metrics
    val holder = remember { ElementHolder() }

    SideEffect {
        if (autosize) holder.element?.let { fitToContent(it, minRows, maxRows) }
    }
    DisposableEffect(autosize, minRows, maxRows) {
        val onResize: (Event) -> Unit = { holder.element?.let { fitToContent(it, minRows, maxRows) } }
        if (autosize) window.addEventListener("resize", onResize)
        onDispose { window.removeEventListener("resize", onResize) }
    }

    FormField(
        label = label,
        modifier = modifier,
        hint = hint,
        help = help,
        error = error,
        required = required,
        reserveMessageSpace = reserveMessageSpace,
    ) { field ->
        Div(
            attrs = Modifier
                .styleModifier {
                    property("position", "relative")
                    property("width", "100%")
                }
                .toAttrs(),
        ) {
            TextArea(
                attrs = Modifier
                    .classNames(ControlClass.FIELD)
                    .fontSize(metrics.fontSize)
                    .styleModifier {
                        property("display", "block")
                        property("width", "100%")
                        property("padding", "${Space.s2} ${metrics.padX}")
                        property("line-height", TEXTAREA_LINE_HEIGHT)
                        property("resize", if (resize && !autosize) "vertical" else "none")
                        if (!autosize) property("min-height", "${minHeightPx}px")
                        if (field.invalid) property("padding-right", trailingPadding(metrics.padX, 1))
                    }
                    .toAttrs {
                        value(value)
                        if (placeholder.isNotEmpty()) placeholder(placeholder)
                        onInput { onValueChange(it.value) }
                        attr("rows", if (autosize) "1" else rows.toString())
                        if (disabled) attr("disabled", "")
                        ariaLabel?.let { attr("aria-label", it) }
                        fieldControl(field)
                        ref { element ->
                            holder.element = element
                            onDispose { if (holder.element === element) holder.element = null }
                        }
                    },
            )
            if (field.invalid) {
                FieldAdornments(metrics.padX, alignTop = true) { InvalidAdornment() }
            }
        }
    }
}
